import { PUSH_SW_MARKER } from './pushNotifications';

/**
 * Limpieza del worker viejo de next-pwa (cacheaba toda la app y degradó la performance).
 * Desregistra TODO service worker que no sea el de push (marcador `?v=push-1` en su
 * scriptURL) y borra Cache Storage / IndexedDB legados. NUNCA desregistra el de push:
 * perdería la suscripción.
 */

/** True si `scriptURL` es el service worker de push (`/sw.js?v=push-1`). */
export const isPushWorkerScript = (scriptURL: string | undefined | null) => {
  if (!scriptURL) return false;
  try {
    const url = new URL(scriptURL, 'http://sw.invalid');
    return (
      url.pathname.endsWith('/sw.js') &&
      url.searchParams.get('v') === new URLSearchParams(PUSH_SW_MARKER).get('v')
    );
  } catch {
    return false;
  }
};

/**
 * Registración legada = tiene algún worker (active/waiting/installing) y ninguno es el de push.
 * Si está reemplazando al viejo (installing = push), se conserva.
 */
export const isLegacyRegistration = (registration: {
  active?: { scriptURL: string } | null;
  waiting?: { scriptURL: string } | null;
  installing?: { scriptURL: string } | null;
}) => {
  const scripts = [
    registration.active,
    registration.waiting,
    registration.installing,
  ]
    .map((worker) => worker?.scriptURL)
    .filter((url): url is string => !!url);
  return scripts.length > 0 && !scripts.some(isPushWorkerScript);
};

export const unregisterLegacyServiceWorkers = async () => {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
    return;
  }
  const registrations = await navigator.serviceWorker.getRegistrations();
  await Promise.all(
    registrations.filter(isLegacyRegistration).map((r) => r.unregister())
  );
};

export const clearLegacyCaches = async () => {
  if (typeof window === 'undefined') return;
  if ('caches' in window) {
    const names = await caches.keys();
    await Promise.all(names.map((name) => caches.delete(name)));
  }
};

/** Limpieza completa (workers + cachés + IndexedDB legados). Best effort, sin lanzar. */
export const cleanupLegacyServiceWorkers = async () => {
  try {
    await unregisterLegacyServiceWorkers();
    await clearLegacyCaches();
    if (typeof indexedDB !== 'undefined' && 'databases' in indexedDB) {
      const databases = await indexedDB.databases();
      databases.forEach((db) => {
        if (db.name) indexedDB.deleteDatabase(db.name);
      });
    }
  } catch {
    // Best effort: el worker viejo simplemente sigue registrado.
  }
};
